import React from 'react';
import { Info, AlertTriangle, Rows3, Trash2, Columns3, Check } from 'lucide-react';
import { RackNode, RowConfig, RackOrientation, useEditorStore } from '../../store/editorStore';
import { FIELD_STYLE, ROW, LABEL, SECTION } from './PropertiesEditorStyles';
import { useConfirmStore } from '../../store/confirmStore';
import { isFacilityType, isArchitectureType } from '../../utils/floorEntities.logic';
import { snapToGrid, TILE_PX, pxToTiles } from '../../utils/cadSnapping.logic';
import { findNearestFreePosition, boxesIntersectOBB, Box2D } from '../../utils/collisionGuard.logic';
import { getRotatedTileSpan } from '../../utils/matrixWizard.logic';
import {
  ALL_RACK_STATUSES,
  ALL_RACK_ROLES,
  RACK_STATUS_METAS,
  RACK_ROLE_METAS,
  normalizeRackStatus,
  normalizeRackRole,
} from '../../utils/rackLifecycle.logic';

interface Props {
  rack: RackNode;
  dcCode?: string;
  rows: Record<string, RowConfig>;
  dcSettings: any;
  racksCountByRow: Record<string, number>;
  onUpdate: (id: string, updates: Partial<RackNode>) => void;
  onDelete: (id: string) => void;
  onGenerateRow: (newRacks: RackNode[]) => void;
}

export const RackInspector: React.FC<Props> = ({ rack, dcCode, rows, dcSettings, racksCountByRow, onUpdate, onDelete }) => {
  const rackRow = rack.rowId ? rows[rack.rowId] : undefined;
  const allRacks = useEditorStore(s => s.racks);

  const otherEntities: Box2D[] = React.useMemo(() => {
    return Object.values(allRacks)
      .filter(r => r.id !== rack.id)
      .map(r => ({
        id: r.id,
        x: r.x,
        y: r.y,
        w: (r.widthM || 0.6) * 100,
        h: (r.depthM || 1.0) * 100,
        rotation: r.rotation,
      }));
  }, [allRacks, rack.id]);

  const candidateBox: Box2D = React.useMemo(() => ({
    id: rack.id,
    x: rack.x,
    y: rack.y,
    w: (rack.widthM || 0.6) * 100,
    h: (rack.depthM || 1.0) * 100,
    rotation: rack.rotation,
  }), [rack.id, rack.x, rack.y, rack.widthM, rack.depthM, rack.rotation]);

  const collidingEntity = React.useMemo(() => {
    return otherEntities.find(e => boxesIntersectOBB(candidateBox, e));
  }, [candidateBox, otherEntities]);

  const collidingRack = collidingEntity ? allRacks[collidingEntity.id] : null;
  const hasCollision = Boolean(collidingEntity);

  const update = (field: keyof RackNode, value: any, extra?: Partial<RackNode>) => {
    const patch: Partial<RackNode> = { [field]: value, ...extra, isDirty: true };
    if (field === 'rotation') patch.orientation = value as RackOrientation;
    onUpdate(rack.id, patch);
  };

  const handleDeleteRack = async () => {
    const entityName = isPillar ? 'колонну' : isFacility ? rack.label || 'объект' : 'стойку';
    const confirmed = await useConfirmStore.getState().confirm({
      title: isPillar ? 'Удаление колонны' : isFacility ? 'Удаление объекта' : 'Удаление стойки',
      message: `Удалить ${entityName} "${rack.label}"?`,
      confirmText: 'Удалить',
      cancelText: 'Отмена'
    });
    if (confirmed) {
      onDelete(rack.id);
    }
  };

  const isFacility = Boolean(rack.isFacility || isFacilityType(rack.rackType));
  const isPillar = Boolean(rack.isBlocked || isArchitectureType(rack.rackType));
  const currentStatus = normalizeRackStatus(rack.status);
  const currentRole = normalizeRackRole(rack.role, rack.rackType);
  const [cascadeToEquipment, setCascadeToEquipment] = React.useState(true);

  return (
    <div style={{ padding: '0 12px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, color: 'var(--color-accent-blue)', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span>{rack.label}</span>
        {isPillar ? (
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(113, 113, 122, 0.2)', color: 'var(--text-secondary)', border: '1px solid rgba(113, 113, 122, 0.4)' }}>
            Колонна
          </span>
        ) : isFacility ? (
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--color-accent-purple-bg)', color: 'var(--color-accent-purple)', border: '1px solid var(--color-accent-purple-border)' }}>
            Инфраструктура
          </span>
        ) : null}
        <span style={{
          fontSize: 9,
          padding: '1px 5px',
          borderRadius: 3,
          background: RACK_STATUS_METAS[currentStatus].badgeBg,
          color: RACK_STATUS_METAS[currentStatus].color,
          border: `1px solid ${RACK_STATUS_METAS[currentStatus].badgeBorder}`,
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          {RACK_STATUS_METAS[currentStatus].labelEn}
        </span>
        <span style={{
          fontSize: 9,
          padding: '1px 5px',
          borderRadius: 3,
          background: RACK_ROLE_METAS[currentRole].badgeBg,
          color: RACK_ROLE_METAS[currentRole].color,
          border: `1px solid ${RACK_ROLE_METAS[currentRole].badgeBorder}`,
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: 700,
        }}>
          {RACK_ROLE_METAS[currentRole].shortCode}
        </span>
        {rack.isDirty && <span style={{ fontSize: 10, color: 'var(--status-warning)', marginLeft: 'auto' }}>● черновик</span>}
      </div>

      {!isPillar && !isFacility && (
        <>
          <div style={ROW}>
            <label style={LABEL}>Ряд</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <select
                style={{ ...FIELD_STYLE, flex: 1, cursor: 'pointer' }}
                value={rack.rowId || ''}
                onChange={e => update('rowId', e.target.value || undefined)}
              >
                <option value="">— не назначен —</option>
                {Object.values(rows).sort((a, b) => a.labelIndex - b.labelIndex).map(row => (
                  <option key={row.id} value={row.id}>
                    {row.name} ({racksCountByRow[row.id] || 0} ст.)
                  </option>
                ))}
              </select>
              {rackRow && (
                <div style={{ width: 20, height: 20, borderRadius: 3, background: rackRow.color, alignSelf: 'center', flexShrink: 0 }} />
              )}
            </div>
          </div>

          {rack.rowId && (
            <div style={ROW}>
              <label style={LABEL} htmlFor="rack-pos">Позиция в ряду</label>
              <input id="rack-pos" style={FIELD_STYLE} type="number" min={1} value={rack.rackPosInRow || 1}
                onChange={e => update('rackPosInRow', parseInt(e.target.value) || 1)} />
            </div>
          )}
          {!rack.rowId && (
            <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', marginTop: -2, marginBottom: 4 }}>
              Выберите ряд из списка, чтобы добавить стойку в существующий ряд
            </div>
          )}
        </>
      )}

      {/* ── Status Lifecycle Chips ── */}
      <div style={ROW}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ ...LABEL, marginBottom: 0 }}>Статус жизненного цикла</label>
          <span style={{ fontSize: 9, color: RACK_STATUS_METAS[currentStatus].color, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
            {RACK_STATUS_METAS[currentStatus].labelEn.toUpperCase()}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 6 }}>
          {ALL_RACK_STATUSES.map(st => {
            const meta = RACK_STATUS_METAS[st];
            const isSel = currentStatus === st;
            return (
              <button
                key={st}
                type="button"
                onClick={() => update('status', st)}
                style={{
                  padding: '5px 4px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontFamily: 'JetBrains Mono, monospace',
                  fontWeight: isSel ? 600 : 400,
                  cursor: 'pointer',
                  border: `1px solid ${isSel ? meta.badgeBorder : 'var(--border-color, #374151)'}`,
                  background: isSel ? meta.badgeBg : 'var(--bg-card, #111827)',
                  color: isSel ? meta.color : 'var(--text-secondary, #9ca3af)',
                  transition: 'all 0.12s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                }}
                title={meta.description}
              >
                {isSel && <Check size={10} />}
                {meta.labelRu}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Functional Role Chips ── */}
      <div style={ROW}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ ...LABEL, marginBottom: 0 }}>Функциональная роль</label>
          <span style={{ fontSize: 9, color: RACK_ROLE_METAS[currentRole].color, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
            [{RACK_ROLE_METAS[currentRole].shortCode}]
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginBottom: 6 }}>
          {ALL_RACK_ROLES.map(rl => {
            const meta = RACK_ROLE_METAS[rl];
            const isSel = currentRole === rl;
            return (
              <button
                key={rl}
                type="button"
                onClick={() => update('role', rl)}
                style={{
                  padding: '4px 2px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontFamily: 'JetBrains Mono, monospace',
                  fontWeight: isSel ? 600 : 400,
                  cursor: 'pointer',
                  border: `1px solid ${isSel ? meta.badgeBorder : 'var(--border-color, #374151)'}`,
                  background: isSel ? meta.badgeBg : 'var(--bg-card, #111827)',
                  color: isSel ? meta.color : 'var(--text-secondary, #9ca3af)',
                  transition: 'all 0.12s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
                title={meta.description}
              >
                {isSel && <Check size={10} />}
                {meta.labelRu}
              </button>
            );
          })}
        </div>
      </div>

      <div style={ROW}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ ...LABEL, marginBottom: 0 }} htmlFor="rack-tenant">Арендатор (Tenant)</label>
          <button
            type="button"
            onClick={() => useEditorStore.getState().setShowRackAllocation(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-accent-blue, #60a5fa)',
              fontSize: 10,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: 0,
            }}
            title="Открыть матрицу распределения стоек по группам"
          >
            <Columns3 size={11} /> Матрица →
          </button>
        </div>

        {/* 1-click segmented quick chips */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginBottom: 6 }}>
          {[
            { key: 'mine', label: 'Своя', color: '#3b82f6', bg: 'rgba(59,130,246,0.18)', border: 'rgba(59,130,246,0.5)' },
            { key: 'other', label: 'Colocation', color: '#a855f7', bg: 'rgba(168,85,247,0.18)', border: 'rgba(168,85,247,0.5)' },
            { key: 'reserved', label: 'Резерв', color: '#f59e0b', bg: 'rgba(245,158,11,0.18)', border: 'rgba(245,158,11,0.5)' },
          ].map(opt => {
            const isSel = (rack.tenant || 'mine') === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => update('tenant', opt.key)}
                style={{
                  padding: '5px 4px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: isSel ? 600 : 400,
                  cursor: 'pointer',
                  border: `1px solid ${isSel ? opt.border : 'var(--border-color, #374151)'}`,
                  background: isSel ? opt.bg : 'var(--bg-card, #111827)',
                  color: isSel ? opt.color : 'var(--text-secondary, #9ca3af)',
                  transition: 'all 0.12s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                }}
              >
                {isSel && <Check size={11} />}
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Cascade tenancy to equipment checkbox */}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            color: 'var(--text-secondary, #9ca3af)',
            marginBottom: 6,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            data-testid="rack-inspector-cascade-checkbox"
            checked={cascadeToEquipment}
            onChange={e => {
              const checked = e.target.checked;
              setCascadeToEquipment(checked);
              update('cascadeTenancy', checked);
            }}
            style={{ accentColor: 'var(--color-accent-blue, #3b82f6)', cursor: 'pointer' }}
          />
          <span>Применить владельца ко всему установленному оборудованию</span>
        </label>

        {/* Custom tenant badge if custom */}
        {rack.tenant && !['mine', 'other', 'reserved'].includes(rack.tenant) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: 'var(--color-accent-blue, #60a5fa)', fontWeight: 500 }}>Кастомный тенант:</span>
            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd' }}>
              {rack.tenant}
            </span>
          </div>
        )}
      </div>

      <div style={ROW}>
        <label style={LABEL} htmlFor="rack-label">Метка / Адрес</label>
        <input id="rack-label" style={FIELD_STYLE} value={rack.label} onChange={e => update('label', e.target.value)} />
      </div>

      <div style={SECTION}>Размеры (физические)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-width">W (м)</label>
          <input id="rack-width" style={FIELD_STYLE} type="number" step={0.05} min={0.4} max={1.2}
            value={Math.round((rack.widthM || 0.6) * 100) / 100}
            onChange={e => update('widthM', parseFloat(e.target.value))} />
        </div>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-depth">D (м)</label>
          <input id="rack-depth" style={FIELD_STYLE} type="number" step={0.05} min={0.5} max={1.5}
            value={Math.round((rack.depthM || 1.0) * 100) / 100}
            onChange={e => update('depthM', parseFloat(e.target.value))} />
        </div>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-height">H (м)</label>
          <input id="rack-height" style={FIELD_STYLE} type="number" step="any" min={1.0} max={3.0}
            value={Math.round((rack.heightM || dcSettings.defaultRackHeightM || 2.0) * 100) / 100}
            onChange={e => update('heightM', parseFloat(e.target.value))} />
        </div>
      </div>
      {!isPillar && !isFacility && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div style={ROW}>
            <label style={LABEL} htmlFor="rack-ucount">U-слоты</label>
            <input id="rack-ucount" style={FIELD_STYLE} type="number" min={1} max={60}
              value={rack.uCount || 42}
              onChange={e => {
                const u = parseInt(e.target.value) || 42;
                update('uCount', u, { heightM: Math.round(u * 44.45) / 1000 });
              }} />
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', alignSelf: 'end', paddingBottom: 2 }}>
            {Math.round(((rack.heightM || 2.0) * 1000 / 44.45))}U из H · 42U = 1.87м
          </div>
        </div>
      )}
      {!isPillar && !isFacility && (
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-power">Лимит мощности (кВт)</label>
          <input id="rack-power" style={FIELD_STYLE} type="number" step={0.5} min={0}
            value={Math.round((rack.max_power_w || 5000) / 100) / 10}
            onChange={e => update('max_power_w', Math.round(parseFloat(e.target.value) * 1000))}
          />
        </div>
      )}
      <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)', marginTop: -4, marginBottom: 10 }}>
        {isPillar
          ? 'Несущая колонна (архитектурное препятствие, 0U)'
          : isFacility
          ? 'Инфраструктурный агрегат (без U-слотов монтажа)'
          : 'Высота 1U = 44.45 мм. Лимит мощности используется в линзе «Мощность» и What-If.'}
      </div>

      {isFacility ? (
        <div style={{
          padding: '8px 10px',
          borderRadius: 5,
          background: 'var(--bg-hover)',
          border: '1px solid var(--border-color)',
          color: 'var(--text-secondary, #9ca3af)',
          fontSize: 11,
          marginTop: 14,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <Info size={13} style={{ flexShrink: 0, color: 'var(--color-accent-blue, #60a5fa)' }} />
          <span>{isPillar ? 'Колонны не содержат слотов монтажа оборудования' : 'Инженерные агрегаты не принимают монтаж IT-серверов'}</span>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, marginBottom: 12 }}>
          {rack.isDirty || !rack.dbId ? (
            <div
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: 'var(--bg-hover)', color: 'var(--text-muted, #6b7280)', padding: '8px 12px', borderRadius: 4,
                fontSize: 13, fontWeight: 600, cursor: 'not-allowed', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
              }}
              title={!rack.dbId ? 'Опубликуйте план, чтобы стойка появилась в базе данных' : 'Сохраните изменения на плане (опубликуйте черновик), чтобы перейти к управлению стойкой'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><line x1="9" y1="4" x2="9" y2="20"></line><line x1="15" y1="4" x2="15" y2="20"></line><line x1="4" y1="12" x2="20" y2="12"></line></svg>
              {!rack.dbId ? 'Управление стойкой (Требуется публикация)' : 'Управление стойкой (Требуется сохранение)'}
            </div>
          ) : (
            <a
              href={`/${dcCode}/racks/${rack.dbId}`}
              target="_blank"
              rel="noreferrer"
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: 'var(--status-normal, #10b981)', color: 'white', textDecoration: 'none', padding: '8px 12px',
                borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><line x1="9" y1="4" x2="9" y2="20"></line><line x1="15" y1="4" x2="15" y2="20"></line><line x1="4" y1="12" x2="20" y2="12"></line></svg>
              Управление стойкой
            </a>
          )}
        </div>
      )}

      <div style={SECTION}>Координаты (CAD и плитка 600×600 мм)</div>
      
      {/* Physical Meters */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-x">X (м)</label>
          <input
            id="rack-x"
            style={FIELD_STYLE}
            type="number"
            step={0.05}
            value={Number((rack.x / 100).toFixed(2))}
            onChange={e => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) update('x', Math.round(v * 100));
            }}
          />
        </div>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-y">Y (м)</label>
          <input
            id="rack-y"
            style={FIELD_STYLE}
            type="number"
            step={0.05}
            value={Number((rack.y / 100).toFixed(2))}
            onChange={e => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) update('y', Math.round(v * 100));
            }}
          />
        </div>
      </div>

      {/* Discrete Tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-tile-x">Плитка X (столбец)</label>
          <input
            id="rack-tile-x"
            style={{ ...FIELD_STYLE, color: '#60a5fa' }}
            type="number"
            step={1}
            min={0}
            value={pxToTiles(rack.x, TILE_PX)}
            onChange={e => {
              const tile = parseInt(e.target.value, 10);
              if (!isNaN(tile) && tile >= 0) {
                update('x', tile * TILE_PX);
              }
            }}
          />
        </div>
        <div style={ROW}>
          <label style={LABEL} htmlFor="rack-tile-y">Плитка Y (ряд)</label>
          <input
            id="rack-tile-y"
            style={{ ...FIELD_STYLE, color: '#60a5fa' }}
            type="number"
            step={1}
            min={0}
            value={pxToTiles(rack.y, TILE_PX)}
            onChange={e => {
              const tile = parseInt(e.target.value, 10);
              if (!isNaN(tile) && tile >= 0) {
                update('y', tile * TILE_PX);
              }
            }}
          />
        </div>
      </div>

      {/* Tile metrics & snap */}
      {(() => {
        const tileSpan = getRotatedTileSpan(rack.widthM || 0.6, rack.depthM || 1.0, rack.rotation || 0);
        const isRotated = rack.rotation === 90 || rack.rotation === 270;
        return (
          <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Занимает: {tileSpan.spanXStr} × {tileSpan.spanYStr} пл. {isRotated ? '(повернута)' : ''}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => {
                  onUpdate(rack.id, {
                    x: snapToGrid(rack.x, TILE_PX),
                    y: snapToGrid(rack.y, TILE_PX),
                    isDirty: true,
                  });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#60a5fa',
                  cursor: 'pointer',
                  fontSize: 10,
                  textDecoration: 'underline',
                }}
                title="Привязать к ближайшей плитке 600×600 мм"
              >
                К сетке
              </button>
              {hasCollision && (
                <button
                  onClick={() => {
                    const freePos = findNearestFreePosition(candidateBox, otherEntities, TILE_PX);
                    onUpdate(rack.id, {
                      x: freePos.x,
                      y: freePos.y,
                      isDirty: true,
                    });
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#34d399',
                    cursor: 'pointer',
                    fontSize: 10,
                    textDecoration: 'underline',
                  }}
                  title="Найти ближайшую свободную плитку"
                >
                  Свободная
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Collision alert badge if occupied */}
      {hasCollision && (
        <div style={{
          padding: '8px 10px',
          borderRadius: 5,
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid var(--status-critical, #ef4444)',
          color: 'var(--status-critical, #ef4444)',
          fontSize: 11,
          marginBottom: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
            <AlertTriangle size={13} style={{ flexShrink: 0 }} />
            <span>
              {collidingRack
                ? `Наложение на "${collidingRack.label || 'объект'}"`
                : 'Пересечение с объектом на сетке'}
            </span>
          </div>
          <div style={{ fontSize: 10, color: '#fca5a5' }}>
            Координаты: плитка [{pxToTiles(rack.x, TILE_PX)}, {pxToTiles(rack.y, TILE_PX)}]
          </div>
          <button
            onClick={() => {
              const freePos = findNearestFreePosition(candidateBox, otherEntities, TILE_PX);
              onUpdate(rack.id, {
                x: freePos.x,
                y: freePos.y,
                isDirty: true,
              });
            }}
            style={{
              alignSelf: 'flex-start',
              marginTop: 2,
              padding: '3px 8px',
              borderRadius: 4,
              background: 'var(--status-critical, #ef4444)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            Сместить на свободную плитку
          </button>
        </div>
      )}

      <div style={{ ...ROW, marginBottom: 14 }}>
        <label style={LABEL}>Поворот (Фронт)</label>
        <div style={{ display: 'flex', gap: 4 }}>
          {[0, 90, 180, 270].map(deg => (
            <button
              key={deg}
              onClick={() => update('rotation', deg)}
              style={{
                flex: 1, padding: '4px 0',
                background: rack.rotation === deg ? 'var(--color-accent-blue-bg, rgba(59,130,246,0.2))' : 'var(--bg-card, #111827)',
                border: rack.rotation === deg ? '1px solid var(--color-accent-blue-border, rgba(59,130,246,0.5))' : '1px solid var(--border-color, #374151)',
                borderRadius: 4, color: rack.rotation === deg ? 'var(--color-accent-blue, #60a5fa)' : 'var(--text-muted, #6b7280)', fontSize: 11, cursor: 'pointer',
              }}
            >
              {deg}°
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: 'var(--bg-card, #111827)', border: '1px solid var(--border-color-subtle, #1f2937)', padding: '10px', borderRadius: '6px', marginBottom: '14px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary, #9ca3af)', marginBottom: 6 }}>ГЕНЕРАТОР РЯДОВ</div>
        <button
          onClick={() => useEditorStore.getState().setShowRowGenerator(true)}
          style={{
            width: '100%',
            padding: '7px 10px',
            background: 'var(--color-accent-blue-bg, rgba(59,130,246,0.12))',
            border: '1px solid var(--color-accent-blue-border, rgba(59,130,246,0.3))',
            color: 'var(--color-accent-blue, #60a5fa)',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Rows3 size={13} /> Конфигуратор рядов и коридоров (R)
        </button>
      </div>

      <button
        onClick={handleDeleteRack}
        style={{
          width: '100%',
          padding: '6px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.4)',
          borderRadius: 5,
          color: '#ef4444',
          fontSize: 12,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <Trash2 size={13} /> {isPillar ? 'Удалить колонну' : isFacility ? `Удалить ${rack.label || 'объект'}` : 'Удалить стойку'}
      </button>
    </div>
  );
};
